import { useState } from 'react';
import { useQueries } from 'react-query';
import { useSelector } from 'react-redux';
import { getAsmCertPaged, getCertCounter, getCertList } from 'services/asm-service';
import { useAuthTenant } from 'store/auth';
import { BlockTitle, CardCounter, ContainerLogo, ContentCardCounter, InfoCertBlock, TitleCardCounter } from '../styles';
import { Bar, BarChart, Legend, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import { DefaultTable } from 'Components/DefaultTable';
import { Pagination, Stack } from '@mui/material';
import { tableHeaderAsmCert } from './table-header-asm-cert';
import { Shimmer } from 'Components/PageLoader/styles';
import { PageLoaderList } from 'Components/PageLoader/List';
import { useTranslation } from 'react-i18next';

export const TabCert = () => {
  const { t } = useTranslation(['asm']);
  const [page, setPage] = useState(1);
  const selectedTenant = useSelector(useAuthTenant);

  const queries = useQueries([
    {
      queryKey: ['getCertCounter'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return null;

        const response = await getCertCounter(selectedTenant.slug);

        if (response?.data && response?.data?.certCounter) {
          return response?.data?.certCounter;
        }
        return 0;
      },
    },
    {
      queryKey: ['getCertList'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return null;

        const response = await getCertList(selectedTenant.slug);

        if (response?.data && response?.data?.certs) {
          return response?.data?.certs;
        }
        return [];
      },
    },
    {
      queryKey: ['getAsmCertPaged', page],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return [];
        const result = await getAsmCertPaged(selectedTenant.slug, page, 6);

        if (result?.data?.data.length > 0) {
          return result?.data;
        }
        return {
          data: [],
          page,
          totalPages: 0,
        };
      },
    },
  ]);

  const [
    {
      data: certCounter,
      isLoading: certCounterLoading,
      // isError: certCounterError
    },
    {
      data: certs,
      isLoading: certsLoading,
      // isError: certsError
    },
    {
      data: pagedAsmCert,
      isLoading: asmCertLoading,
      // isError: asmCertError
    },
  ] = queries;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { name, qt } = payload[0].payload;
      return (
        <div style={{ backgroundColor: '#ffffff', padding: '5px', border: '1px solid #ccc' }}>
          <p>{`${name}: ${qt}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {/* Container para o card contador e gráfico lado a lado */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'row', 
        gap: '24px', 
        marginBottom: '40px',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        width: '100%',
        maxWidth: '100%',
        padding: '0'
      }} data-tour="cert-overview">
        <CardCounter
          style={{
            minWidth: '220px',
            flexShrink: 0,
            height: '300px',
            background: 'linear-gradient(180deg, #F9FBFF 0%, #FFFFFF 100%)',
            border: '1px solid #EEF2FF',
            boxShadow: '0px 8px 24px #EEF2FF',
            padding: '20px',
          }}
        >
          {!certCounterLoading && (
            <>
              <TitleCardCounter style={{ color: '#0039C5', letterSpacing: '0.3px', marginBottom: '8px' }}>
                {t('asm:tabCert.cardTitle')}
              </TitleCardCounter>
              <ContentCardCounter style={{ fontSize: '44px', gap: '12px', alignItems: 'center' }}>
                {certCounter}
                <ContainerLogo style={{ width: '40px', height: '40px' }}>
                  <img src="img/icons/flag.png" alt="logo de uma bandeira" />
                </ContainerLogo>
              </ContentCardCounter>
            </>
          )}
          {certCounterLoading && (
            <Shimmer></Shimmer>
          )}
        </CardCounter>
        
        <InfoCertBlock style={{ flex: '1', minWidth: '400px', height: '300px' }} data-tour="cert-issuers">
          {!certsLoading && (
            <>
              <BlockTitle>{t('asm:tabCert.chartIssuers')}</BlockTitle>
              <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={400}>
                <BarChart
                  layout={'vertical'}
                  width={500}
                  height={300}
                  data={certs}
                  margin={{
                    top: 0,
                    right: 16,
                    left: 0,
                    bottom: 0,
                  }}
                  maxBarSize={certs.reduce((max, obj) => (obj.qa > max.qt ? obj : max), [0])[0].qt}
                >
                  <XAxis type="number" domain={[0, 'dataMax + 1']} axisLine={false} />
                  <YAxis dataKey={'name'} type="category" width={120} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="top" />
                  <Bar name={t('asm:tabCert.chartIssuers')} dataKey="qt" barSize={24} fill="rgb(134,239,172)" />
                </BarChart>
              </ResponsiveContainer>
            </>
          )}
          {certsLoading && <Shimmer style={{ height: '200px', width: '100%' }}></Shimmer>}
        </InfoCertBlock>
      </div>
      {!asmCertLoading && (
        <>
          <div data-tour="cert-table">
          <DefaultTable
            rows={tableHeaderAsmCert(t)}
            cells={pagedAsmCert?.data || []}
            link=""
            showNoDataLine
            applyMB={pagedAsmCert?.totalPages < 1}
          />
          </div>
          {pagedAsmCert?.totalPages > 0 && (
            <Stack sx={{ justifyContent: 'center', flexDirection: 'row', marginBlock: '16px', marginInline: 'auto' }} data-tour="cert-pagination">
              <Pagination
                count={pagedAsmCert.totalPages}
                onChange={(_, selectedPage) => {
                  setPage(selectedPage);
                }}
                color="primary"
                page={page}
                showFirstButton
                showLastButton
              />
            </Stack>
          )}
        </>
      )}
      {asmCertLoading && <PageLoaderList />}
    </>
  );
};
